import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useCollabClubArtistsSearch } from 'hooks/swrRequests/useCollabClubArtistsSearch';
import useOnScreen from 'hooks/useOnScreen';
import { useUserData } from 'hooks/useUserData';
import { debounce } from 'lodash';
import { Heart, Loader2, Music, Search, Users, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Artist, CcConnectionStats } from 'types';
import { ArtistCard } from './artist-card';
import { ArtistMultiFilters } from './artist-multi-filters';
import type {
  ArtistMultiFilters as ArtistMultiFiltersType,
  MultiFilterKey,
} from './multi-filter-types';
import { DEFAULT_MULTI_FILTERS, isAllSelected } from './multi-filter-types';
import { SelectedArtistsFloat } from './selected-artists-float';

interface ArtistExplorerProps {
  selectedArtists: Map<string, Artist>;
  onToggleSelection: (artistId: string, artist: Artist) => void;
  likedArtists: Set<string>;
  onToggleLike: (artistId: string) => void;
  showOnlyLiked: boolean;
  onToggleShowOnlyLiked: (show: boolean) => void;
  onConnectSelected: () => void;
  connectionStats?: CcConnectionStats | null;
  onArtistsLoaded?: (artists: Artist[]) => void;
}

export function ArtistExplorer({
  selectedArtists,
  onToggleSelection,
  likedArtists,
  onToggleLike,
  showOnlyLiked,
  onToggleShowOnlyLiked,
  onConnectSelected,
  connectionStats,
  onArtistsLoaded,
}: ArtistExplorerProps) {
  const { t } = useTranslation('collab-club');
  const { userData } = useUserData();
  const [filters, setFilters] = useState<ArtistMultiFiltersType>(DEFAULT_MULTI_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Ref for infinite scroll loader
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isLoadMoreVisible = useOnScreen(loadMoreRef, '100px'); // No debug, no debouncing

  // Debounce search input
  const debouncedSetSearch = useMemo(
    () =>
      debounce((query: string) => {
        setDebouncedSearchQuery(query);
      }, 300),
    []
  );

  // Parse distance filter value to maxDistance number
  const parseDistanceToMaxDistance = (distanceValue: string): number | undefined => {
    if (distanceValue === 'all') return undefined;

    // Expected formats: "within-1000", "within-5000", etc.
    if (distanceValue.startsWith('within-')) {
      // Extract number after "within-" (e.g., "within-1000" -> 1000)
      const distance = parseInt(distanceValue.replace('within-', ''));
      return isNaN(distance) ? undefined : distance;
    }

    return undefined;
  };

  // Build search parameters from filters
  const searchParams = useMemo(() => {
    const params: any = {
      search: debouncedSearchQuery,
      filters: {},
    };

    // Add genre filters
    if (!isAllSelected(filters.genres)) {
      params.filters.genres = filters.genres.filter((g) => g !== 'all');
    }

    // Add skills filters
    if (!isAllSelected(filters.skills)) {
      params.filters.skills = filters.skills.filter((s) => s !== 'all');
    }

    // Add country filters
    if (!isAllSelected(filters.countries)) {
      params.filters.countries = filters.countries.filter((c) => c !== 'all');
    }

    // Add monthly listeners filter
    if (filters.monthlyListenersBucket && filters.monthlyListenersBucket !== 'all') {
      params.filters.monthlyListenersBucket = filters.monthlyListenersBucket;
    }

    // Always pass coordinates if available (for facets)
    if (userLocation) {
      params.coordinates = userLocation;

      // Add distance filter if not "all"
      const maxDistance = parseDistanceToMaxDistance(filters.distance);
      if (maxDistance !== undefined) {
        params.filters.maxDistance = maxDistance;
      }
    }

    return params;
  }, [debouncedSearchQuery, filters, userLocation]);

  // Use search hook
  const { artists, facets, isLoading, isLoadingMore, error, hasMore, loadMore, totalItems } =
    useCollabClubArtistsSearch(searchParams);

  // Filter for liked artists if needed (client-side)
  const displayedArtists = useMemo(() => {
    if (showOnlyLiked) {
      return artists.filter((artist) => likedArtists.has(artist.id.toString()));
    }
    return artists;
  }, [artists, showOnlyLiked, likedArtists]);

  // Notify parent when artists change
  useEffect(() => {
    if (onArtistsLoaded) {
      onArtistsLoaded(artists);
    }
  }, [artists]); // Don't include onArtistsLoaded in deps to avoid loops

  // Trigger load more when sentinel is visible
  useEffect(() => {
    if (isLoadMoreVisible && hasMore && !isLoadingMore && !isLoading) {
      loadMore();
    }
  }, [isLoadMoreVisible, hasMore, isLoadingMore, isLoading]);

  // Update debounced search when search query changes
  useEffect(() => {
    debouncedSetSearch(filters.searchQuery);
  }, [filters.searchQuery, debouncedSetSearch]);

  const handleFilterChange = (key: MultiFilterKey, values: string[]) => {
    // Ensure we never have an empty array - default to ['all']
    const finalValues = values.length === 0 ? ['all'] : values;
    setFilters((prev) => ({ ...prev, [key]: finalValues }));
    // Don't call reset() here - the useEffect in the hook will handle it
  };

  const handleDistanceChange = (distance: string) => {
    setFilters((prev) => ({ ...prev, distance }));
    // Don't call reset() here - the useEffect in the hook will handle it
  };

  const handleMonthlyListenersChange = (bucket: string) => {
    setFilters((prev) => ({ ...prev, monthlyListenersBucket: bucket }));
    // Don't call reset() here - the useEffect in the hook will handle it
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_MULTI_FILTERS);
    // Don't call reset() here - the useEffect in the hook will handle it
  };

  const handleSearchChange = (query: string) => {
    setFilters((prev) => ({ ...prev, searchQuery: query }));
  };

  const handleSearchClick = () => {
    setShowFilters(true);
  };

  const clearSearch = () => {
    handleSearchChange('');
  };

  const hasActiveFilters =
    !isAllSelected(filters.genres) ||
    filters.distance !== 'all' ||
    !isAllSelected(filters.countries) ||
    filters.monthlyListenersBucket !== 'all' ||
    !isAllSelected(filters.skills) ||
    filters.searchQuery !== '';

  const handleSpotifyClick = (artist: Artist) => {
    if (!artist.spotifyUrl) return;
    window.open(artist.spotifyUrl, '_blank');
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">{t('discover_artists')}</h1>
        <p className="max-w-3xl text-lg whitespace-pre-line">{t('discover_artists_description')}</p>
        <SelectedArtistsFloat
          selectedArtists={selectedArtists}
          onConnectSelected={onConnectSelected}
          connectionStats={connectionStats}
        />
      </div>

      {/* Search Section */}
      <div className="mb-6">
        {/* Search Input */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            placeholder={t('search_placeholder')}
            value={filters.searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onClick={handleSearchClick}
            className="pl-12 pr-12 h-16 text-lg border-2 border-blue-200 focus:border-blue-400 focus:ring-0 rounded-xl bg-blue-50/30 box-border"
          />
          {filters.searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Multi-Filters */}
        {showFilters && (
          <ArtistMultiFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onDistanceChange={handleDistanceChange}
            onMonthlyListenersChange={handleMonthlyListenersChange}
            onClearFilters={handleClearFilters}
            onClose={() => setShowFilters(false)}
            userLocation={userLocation}
            onLocationUpdate={setUserLocation}
            facets={facets || undefined}
          />
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {showOnlyLiked
              ? t('liked_artists', { count: displayedArtists.length })
              : t('artists_found', { count: totalItems || displayedArtists.length })}
          </span>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
            >
              <X className="h-3 w-3" />
              {t('clear_all_filters')}
            </button>
          )}
        </div>
        <Button
          variant={showOnlyLiked ? 'default' : 'outline'}
          size="sm"
          onClick={() => onToggleShowOnlyLiked(!showOnlyLiked)}
          className="flex items-center gap-2"
        >
          <Heart className={`h-4 w-4 ${showOnlyLiked ? 'fill-current' : ''}`} />
          {t('liked_only', { count: likedArtists.size })}
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-12">
          <p className="text-red-600">{t('error_loading_artists')}</p>
        </div>
      )}

      {/* Artists Grid */}
      {!isLoading && !error && (
        <>
          {displayedArtists.length === 0 ? (
            <div className="text-center py-12">
              <Music className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('no_artists_found')}</h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displayedArtists.map((artist) => (
                <ArtistCard
                  key={artist.id}
                  artist={artist}
                  isSelected={selectedArtists.has(artist.id.toString())}
                  isLiked={likedArtists.has(artist.id.toString())}
                  isSelectionDisabled={
                    !connectionStats ||
                    connectionStats.remainingConnections === 0 ||
                    (!selectedArtists.has(artist.id.toString()) &&
                      selectedArtists.size >= connectionStats.remainingConnections)
                  }
                  isOwnArtist={artist.userId != null && artist.userId === userData.id}
                  onToggleSelection={(artistId) => onToggleSelection(artistId, artist)}
                  onToggleLike={onToggleLike}
                  onSpotifyClick={handleSpotifyClick}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Load More Sentinel - Always render for consistent ref */}
      <div
        ref={loadMoreRef}
        className={`flex items-center justify-center ${hasMore ? 'h-20 pt-8' : 'h-0'}`}
      >
        {isLoadingMore && (
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            <span className="text-gray-600">{t('loading_more')}</span>
          </div>
        )}
      </div>

      {/* End of Results */}
      {!isLoading && !hasMore && displayedArtists.length > 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">{t('end_of_results')}</div>
      )}
    </div>
  );
}
